// Frontend hex rendering for the shared color vocabulary. The vocabulary (which
// families and stops exist) lives in shared/configs/colors; this layer resolves
// it to #rrggbb hex for JS consumers (Motion, canvas). Tailwind v4 ships oklch;

import colors from 'tailwindcss/colors';
import {
  COLOR_STOPS,
  type ColorStop,
  type ColorFamily,
} from '@airhead-games/shared/configs';

// Re-export the shared vocabulary so existing `@/styles/colorStops` consumers keep working.
export { COLOR_STOPS };
export type { ColorStop, ColorFamily };

type ColorScale = Record<ColorStop, string>;

const pickTailwindScale = (scale: Record<ColorStop, string>): ColorScale => ({
  '50': scale['50'],
  '100': scale['100'],
  '200': scale['200'],
  '300': scale['300'],
  '400': scale['400'],
  '500': scale['500'],
  '600': scale['600'],
  '700': scale['700'],
  '800': scale['800'],
  '900': scale['900'],
  '950': scale['950'],
});

export const COLOR_SCALES: Record<ColorFamily, ColorScale> = {
  neutral: pickTailwindScale(colors.neutral),
  red: pickTailwindScale(colors.red),
  orange: pickTailwindScale(colors.orange),
  amber: pickTailwindScale(colors.amber),
  yellow: pickTailwindScale(colors.yellow),
  lime: pickTailwindScale(colors.lime),
  green: pickTailwindScale(colors.green),
  emerald: pickTailwindScale(colors.emerald),
  teal: pickTailwindScale(colors.teal),
  cyan: pickTailwindScale(colors.cyan),
  sky: pickTailwindScale(colors.sky),
  blue: pickTailwindScale(colors.blue),
  indigo: pickTailwindScale(colors.indigo),
  violet: pickTailwindScale(colors.violet),
  purple: pickTailwindScale(colors.purple),
  fuchsia: pickTailwindScale(colors.fuchsia),
  pink: pickTailwindScale(colors.pink),
  rose: pickTailwindScale(colors.rose),
};

/**
 * OKLCH → sRGB hex for JS consumers (Motion, canvas) that can't mix modern CSS
 * colors. Hex specifically: stroke styles relay these values as `colorHex`,
 * which the draw-relay schema validates as #rrggbb.
 */
function oklchToHexString(color: string): string {
  const match = color.match(
    /oklch\(\s*([\d.]+%?)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.]+%?))?\s*\)/i
  );
  if (!match) return color;

  let l = parseFloat(match[1]);
  if (match[1].includes('%')) l /= 100;
  const c = parseFloat(match[2]);
  const h = parseFloat(match[3]);
  let alpha = match[4] ? parseFloat(match[4]) : 1;
  if (match[4]?.includes('%')) alpha /= 100;

  const hRad = (h * Math.PI) / 180;
  const a = c * Math.cos(hRad);
  const b = c * Math.sin(hRad);
  const l_ = l + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = l - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = l - 0.0894841775 * a - 1.291485548 * b;
  const l3 = l_ ** 3;
  const m3 = m_ ** 3;
  const s3 = s_ ** 3;

  const toSrgb = (v: number) => {
    const clamped = Math.max(0, Math.min(1, v));
    const linear =
      clamped <= 0.0031308
        ? 12.92 * clamped
        : 1.055 * Math.pow(clamped, 1 / 2.4) - 0.055;
    return Math.round(linear * 255);
  };

  const r = toSrgb(4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3);
  const g = toSrgb(-1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3);
  const bVal = toSrgb(
    -0.0041960863 * l3 - 0.7034186147 * m3 + 1.707614701 * s3
  );

  const hex = (v: number) => v.toString(16).padStart(2, '0');
  return alpha < 1
    ? `#${hex(r)}${hex(g)}${hex(bVal)}${hex(Math.round(alpha * 255))}`
    : `#${hex(r)}${hex(g)}${hex(bVal)}`;
}

export const getColorStop = (family: ColorFamily, stop: ColorStop): string => {
  const raw = COLOR_SCALES[family][stop];
  return raw.startsWith('oklch(') ? oklchToHexString(raw) : raw;
};
